import React, { useState, useEffect } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import { Alert } from "antd";
import ProtectedRoute from "./auth/ProtectedRoute.jsx";
import { getData } from "./config/api-connect";
import { LeaveProvider, LeaveContext } from "./contexts/LeaveContext";
import { PermissionProvider } from "./contexts/PermissionContext";
import Login from "./pages/login";
import NotFound from "./pages/pageNotFound";
import SideBar from "./components/sideBar";
import AddContract from "./pages/contract/addContract";
import ContractInfo from "./pages/contract/contract";
import Overview from "./pages/overview";
import Revenue from "./pages/revenue";
import Collection from "./pages/collection";
import AddExpense from "./pages/expense";
import AddBuddyData from "./pages/buddyData";
import SalaryDetails from "./pages/salaryDetails";
import BuddyDetails from "./pages/buddyInfo";
import ExpenseDetails from "./pages/expenseDetail";
import ContractTermAddition from "./pages/contract/contractTerm";
import Dashboard from "./pages/dashboard";
import Batches from "./pages/batches";
import Coaches from "./pages/coaches/coaches";
import Attendance from "./pages/batches/attendance.jsx";
import Leads from "./pages/leads";
import Reports from "./pages/reports";
import CollectionList from "./pages/collectionList";
import BatchDetails from "./pages/batchDetails";
import AttendanceSummary from "./pages/batchDetails/attendanceSummary";
import RevenueDetails from "./pages/customerList/revenueDetails";
import CollectionDetails from "./pages/customerList/collectionDetails";
import LeadList from "./pages/customerList/leadList";
import Customers from "./pages/customers";
import RenewalAnalysis from "./pages/customers/renewalAnalysis";
import RenewalDrilldown from "./pages/customers/renewalDrilldown";
import CustomerFeedback from "./pages/customers/customerFeedback";
import RetentionAnalysis from "./pages/customers/retentionAnalysis";
import NewCustomers from "./pages/customers/newCustomers";
import CustomerSummary from "./pages/customers/customerSummary";
import CustomerList from "./pages/customerList/customerList";
import UnverifiedCollections from "./pages/collectionList/unverifiedCollections";
import TotalCollections from "./pages/collectionList/totalCollections";
import CollectionSummary from "./pages/collectionList/collectionSummary";
import PipelineDrilldown from "./pages/leads/pipelineDrilldown";
import LeadsProcessed from "./pages/leads/leadsProcessed";
import LeadsDetails from "./pages/leads/leadsList";
import RevenueSummary from "./pages/revenue/revenueSummary";
import CollectionListSummary from "./pages/collectionList/collectionListSummary";
import Careers from "./pages/careers/jobListing.jsx";
import ApplicantList from "./pages/careers/applicantList.jsx";
import AddJob from "./pages/careers/addJob.jsx";
import EditJob from "./pages/careers/editJob.jsx";
import Leaves from "./pages/leaves/leaveManagement.jsx";
import LeaveApplications from "./pages/leaves/leaveApplications.jsx";
import IDCards from "./pages/visitors";
import VisitorList from "./pages/visitors/visitorList";
import BuddyAttendance from "./pages/attendance/buddyAttendance";
import StaffManagement from "./pages/attendance/staffManagement";
import Reimbursement from "./pages/reimbursement";
import Spinner from "./components/spinner";

function App() {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeaveAlert, setShowLeaveAlert] = useState(true);

  function LeaveAlert({ visible, onClose }) {
    const navigate = useNavigate();
    const { pendingLeaveCount } = React.useContext(LeaveContext) || { pendingLeaveCount: 0 };
    if (!visible || !pendingLeaveCount) return null;
    
    const handleAlertClick = (e) => {
      // Don't navigate if close button is clicked
      if (e.target.closest('.ant-alert-close-icon')) return;
      navigate('/leaveApplications');
      onClose();
    };
    
    return (
      <AlertContainer onClick={handleAlertClick} style={{ cursor: 'pointer' }}>
        <Alert
          // message="Leave request pending"
          description={
            pendingLeaveCount === 1
              ? "A team member has applied for leave. Please review it."
              : `${pendingLeaveCount} team members have applied for leave. Please review them.`
          }
          type="warning"
          closable
          onClose={onClose}
          style={{background:'#fffbe6',border: '1px solid #ffe58f',color: '#d48806',borderRadius:5}}
        />
      </AlertContainer>
    );
  }

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await getData("/auth/auth_status");
        const isLoggedIn = response.success && response.logged_in;
        setIsUserLoggedIn(isLoggedIn);
        
        if (isLoggedIn) {
          localStorage.setItem('access_id', response.user.id);
          localStorage.setItem('user_role', response.user.role);
          localStorage.setItem('user_name', response.user.name);
        }
      } catch (error) {
        console.log("User not logged in:", error);
        setIsUserLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };

    checkLoginStatus();
  }, []);

  if (loading) {
    return <MainContainer><LoadingContainer><Spinner /></LoadingContainer></MainContainer>;
  }

  return (
    <Router>
      <LeaveProvider>
      <MainContainer>
        {isUserLoggedIn ? (
          <PermissionProvider>
            <SideBar />
            <ContainerBody>
              <LeaveAlert visible={showLeaveAlert} onClose={() => setShowLeaveAlert(false)} />
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/revenue" element={<Revenue />} />
                <Route
                  path="/collection"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionList />
                    </ProtectedRoute>
                  }
                />
                <Route path="/addContract" element={<AddContract />} />
                <Route path="/contractInfo" element={<ContractInfo />} />
                <Route path="/addExpense" element={<AddExpense />} />
                <Route
                  path="/addcontractterms"
                  element={<ContractTermAddition />}
                />
                <Route path="/salaryDetails" element={<SalaryDetails />} />
                <Route path="/buddyData" element={<AddBuddyData />} />
                <Route path="/buddyinfo" element={<BuddyDetails />} />
                <Route path="/expenseDetails" element={<ExpenseDetails />} />
                <Route
                  path="/batches/attendance-summary/:venue_id/:game_level"
                  element={<AttendanceSummary />}
                />
                <Route
                  path="/batches/utilization/:venue_id/:game_level"
                  element={<BatchDetails />}
                />
                <Route
                  path="/batches/attendance/:venue_id/:game_level"
                  element={<BatchDetails />}
                />
                <Route path="/batches/utilization" element={<Batches />} />
                <Route path="/batches/attendance" element={<Attendance />} />
                <Route path="/coaches" element={<Coaches />} />
                <Route
                  path="/reimbursement"
                  element={
                    <ProtectedRoute permission="access_finance_reimbursements">
                      <Reimbursement />
                    </ProtectedRoute>
                  }
                />
                <Route path="/lead-pipeline" element={<Leads />} />
                <Route path="/hot-leads" element={<Leads />} />
                <Route path="/leads-generated" element={<Leads />} />
                <Route path="/leads-processed" element={<Leads />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/customers" element={<Customers />} />
                <Route
                  path="/batchDetails/:venue_id/:game_level"
                  element={<BatchDetails />}
                />
                <Route
                  path="/revenueList/:venue_id"
                  element={<RevenueDetails />}
                />
                <Route
                  path="/collectionList/:venue_id"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionDetails />
                    </ProtectedRoute>
                  }
                />
                <Route path="/leadList/:venue_id" element={<LeadList />} />
                <Route
                  path="/customers/:venue_id"
                  element={<CustomerSummary />}
                />
                <Route
                  path="/renewal-analysis/"
                  element={<RenewalAnalysis />}
                />
                <Route
                  path="/retention-analysis/"
                  element={<RetentionAnalysis />}
                />
                <Route
                  path="/renewal-analysis/:type/:monthYear"
                  element={<RenewalDrilldown />}
                />
                <Route
                  path="/customer-feedback"
                  element={<CustomerFeedback />}
                />
                <Route
                  path="/retention-analysis/new/:monthYear"
                  element={<NewCustomers />}
                />
                <Route
                  path="/collection-summary/:venue_id/:venue_name"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/collections/unverified"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <UnverifiedCollections />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/collections/total"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <TotalCollections />
                    </ProtectedRoute>
                  }
                />
                <Route path="/customerList" element={<CustomerList />} />
                <Route path="/leadsProcessed/" element={<LeadsProcessed />} />
                <Route
                  path="/lead-pipeline/details"
                  element={<PipelineDrilldown />}
                />
                <Route path="/leadDetails/" element={<LeadsDetails />} />
                <Route
                  path="/revenue/:venue_id/:type"
                  element={<RevenueSummary />}
                />
                <Route
                  path="/collection/:venue_id/:type"
                  element={
                    <ProtectedRoute excludeRoles={["Partner","Web Partner","Technichal Partner"]}>
                      <CollectionListSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/careers"
                  element={
                    <ProtectedRoute
                      roles={[
                        "HR",
                        "Delivery Manager",
                        "Trespasser",
                        "Lead Buddy",
                        "Marketing Manager"
                      ]}
                    >
                      <Careers />
                    </ProtectedRoute>
                  }
                />
                <Route path="/careers/:jobId" element={<ApplicantList />} />
                <Route path="/careers/add" element={<AddJob />} />
                <Route path="/careers/edit/:id" element={<EditJob />} />
                <Route path="/leaves" element={<Leaves />} />
                <Route
                  path="/leaveApplications"
                  element={<LeaveApplications />}
                />
                <Route path="/visitors/id-cards" element={<IDCards />} />
                <Route path="/visitors/list" element={<VisitorList />} />
                <Route
                  path="/hr/staff-attendance"
                  element={<BuddyAttendance />}
                />
                <Route
                  path="/hr/staff-management"
                  element={
                    <ProtectedRoute
                      roles={["HR", "Delivery Manager", "Trespasser"]}
                    >
                      <StaffManagement />
                    </ProtectedRoute>
                  }
                />
                {/* NotFound route moved outside the conditional rendering */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ContainerBody>
          </PermissionProvider>
        ) : (
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        )}
      </MainContainer>
      </LeaveProvider>
    </Router>
  );
}

export default App;

export const MainContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const ContainerBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

export const AlertContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 16px;
  width: 320px;
  z-index: 1100;
  pointer-events: auto;
`;
